import PropTypes from 'prop-types';
import { useContext, useMemo, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import useFindFamilies from '../../../app-stats/services/data/useFindFamilies';
import useFindUsers from '../../../app-stats/services/data/useFindUsers';
import useFindCustomers from '../../../app-stats/services/data/useFindCustomers';
import LogsPageContext from './LogsPageContext';
import OverlayContext, { Overlays } from '../../containers/OverlayContext';
import { BREAKPOINTS } from '../../services/breakpoints';
import SearchBoxMenuItemType from '../SearchBox/SearchBoxMenuItemType';

function getSearchResultItems(users, customers, families, MAX_SEARCH_RESULTS_PER_TYPE) {
    const items = [];
    users.slice(0, MAX_SEARCH_RESULTS_PER_TYPE).forEach((user) => {
        items.push({
            key: `client_${user.id}`,
            label: user.label,
            type: SearchBoxMenuItemType.MENU_ITEM,
        });
    });
    if (users.length !== 0 && customers.length !== 0) {
        items.push({
            key: 'divider_0',
            label: 'divider',
            type: SearchBoxMenuItemType.DIVIDER,
        });
    }
    customers.slice(0, MAX_SEARCH_RESULTS_PER_TYPE).forEach((customer) => {
        items.push({
            key: `customer_${customer.id}`,
            label: customer.label,
            type: SearchBoxMenuItemType.MENU_ITEM,
        });
    });
    if (customers.length !== 0 && families.length !== 0) {
        items.push({
            key: 'divider_1',
            label: 'divider',
            type: SearchBoxMenuItemType.DIVIDER,
        });
    }
    families.slice(0, MAX_SEARCH_RESULTS_PER_TYPE).forEach((family) => {
        items.push({
            key: `family_${family.id}`,
            label: family.label,
            type: SearchBoxMenuItemType.MENU_ITEM,
        });
    });
    return items;
}
export default function LogsPageContextConsumer({ className, placeholder, children }) {
    const originalSearchText = useRef('');
    const {
        setPage,
        selectedItem,
        setSelectedItem,
        searchText,
        setSearchText,
        MAX_SEARCH_RESULTS_PER_TYPE,
        showSearchResults,
        setShowSearchResults,
    } = useContext(LogsPageContext);
    const [users] = useFindUsers(searchText);
    const [customers] = useFindCustomers(searchText);
    const [families] = useFindFamilies(searchText);
    const items = getSearchResultItems(users, customers, families, MAX_SEARCH_RESULTS_PER_TYPE);
    const { setOverlay } = useContext(OverlayContext);
    const shouldNotUseMobileOverlay = useMediaQuery({ minWidth: BREAKPOINTS.SMALL.min });
    const overlayType = Overlays.SEARCH_LOGS;
    const handleBlur = () => {
        if (shouldNotUseMobileOverlay && !selectedItem && !searchText) {
            setSearchText(originalSearchText.current);
        }
    };
    const handleSetInputSearchText = (newValue) => {
        if (newValue === '') {
            if (selectedItem) {
                setSelectedItem(null);
            }
            setSearchText('');
            originalSearchText.current = '';
        } else if (newValue !== 'not-found') {
            setSearchText(newValue);
        }
    };

    const handleSearchItemSelected = (newSelectedValue) => {
        let newSelectedItem;

        const [type, newSelectedId] = newSelectedValue.split('_');

        switch (type) {
            case 'customer':
                newSelectedItem = customers.find(({ id }) => id === Number(newSelectedId));
                break;
            case 'client':
                newSelectedItem = users.find(({ id }) => id === Number(newSelectedId));
                break;
            case 'family':
                newSelectedItem = families.find(({ id }) => id === Number(newSelectedId));
                break;
            default:
                newSelectedItem = null;
                break;
        }
        if (newSelectedItem) {
            setSelectedItem({
                type,
                id: newSelectedItem.id,
                label: newSelectedItem.label,
                value: newSelectedValue,
            });
            originalSearchText.current = newSelectedItem.label;
            handleSetInputSearchText(newSelectedItem.label);
        } else {
            originalSearchText.current = '';
            handleSetInputSearchText('');
        }
        setPage(0);
        if (!shouldNotUseMobileOverlay) {
            setOverlay(null);
        }
    };

    const handleSearchTextChange = (newValue) => {
        if (newValue === '') {
            originalSearchText.current = '';
        }
        handleSetInputSearchText(newValue);
    };

    const searchWords = useMemo(() => {
        return searchText?.split(' ') || [];
    }, [searchText]);

    return children({
        className,
        placeholder,
        searchText,
        searchWords,
        items,
        overlayType,
        showSearchResults,
        setShowSearchResults,
        selectedValue: selectedItem?.value,
        onSearchTextChange: (newValue) => handleSearchTextChange(newValue),
        onItemSelected: (newSelectedValue) => handleSearchItemSelected(newSelectedValue),
        onBlur: handleBlur,
    });
}
LogsPageContextConsumer.propTypes = {
    children: PropTypes.func,
    className: PropTypes.string,
    placeholder: PropTypes.string,
};
